import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  Reducer,
  useContext,
  useReducer,
} from "react";

import { useAuth } from "@hl/shared-features/lib/features/auth/AuthContext";
import { createFormContext, UseFormReturnType } from "@mantine/form";
import { Entry } from "@zip.js/zip.js";
import { ethers } from "ethers";
import { isEmpty } from "lodash";

import {
  _CollectionType,
  CapturePreviewTriggerType,
  DraftProgress,
  GenerativeEditionType,
  NftContractStandard,
  PriceType,
  SupportedMediaType,
  TimeUnit,
  TokenDistributionType,
} from "apollo/graphql.generated";
import { getPriceDropIntervalTime } from "utils/duthAuctions";

// Internal logic and state management

export type ContractDraft =
  | {
      type: "new";
      edited: boolean;
      data: {
        name: string;
        symbol: string;
        usingOpenseaBlocklist: boolean;
        collectionLogoImage: string;
        contractStandard: NftContractStandard;
      };
    }
  | {
      type: "existing";
      data: { contractId: string; contractStandard?: NftContractStandard };
    };

export interface CaptureSettings {
  trigger: CapturePreviewTriggerType;
  delay?: number;
  viewPort: { height: number; width: number };
  selector?: string;
  selectorType?: string;
}

export interface CreateCollectionForm {
  collectionType: _CollectionType;
  gateId: string | null;
  withoutMintVector: boolean;
  metadata: {
    mediaUrl: string | null;
    coverMediaUrl: string | null;
    mediaType: SupportedMediaType | "zip";
    mediaFiles?: string[];
    traits: Record<string, string>;
    seriesSize: number | null;
    reveal: boolean;
    showAllMetadata: boolean;
    previewImages: { url: string; type: string; name: string }[];
    generateNameTrait?: boolean;
  };
  collection: {
    name: string | null;
    description: string | null;
    chainId: string;
    size: number | null;
    categories?: string[] | null;
    tags?: { slug: string; name: string }[] | null;
  };
  contract: ContractDraft;
  distribution: {
    type: TokenDistributionType;
    price?: number;
    currency: string;
    saleDateFrom: Date;
    saleDateTo: Date | null;
    priceType: PriceType;
    scheduledPrices?: string[] | null;
    priceDropInterval?:
      | {
          unit: TimeUnit;
          value: number;
        }[]
      | null;
    isDirectMint: boolean;
    isFreePrice: boolean;
    minBid?: number;
    rebateWinningBids?: boolean;
    sponsoredMintsEnabled?: boolean | null;
  };
  moduleSettings: {
    shouldLimitNumberOfMintPerWallet: boolean;
    limitPerWallet: number | null;
    nonTransferable: boolean;
    royalty: {
      royaltyAmount: number;
      payoutEnsName: string | null;
      payoutEoa: string | null;
      address: string | null; // Can be either an EOA or ENS
    };
    customPaymentRecipient: {
      ensName: string | null;
      eoa: string | null;
      address: string | null; // Can be either an EOA or ENS
    };
  };
  generativeDetails?: {
    curateOutput?: boolean | null;
    isGPURendering?: boolean | null;
    allowedHashes?: string[] | null;
    captureSettings?: CaptureSettings;
    editionType?: GenerativeEditionType | null;
  };
}

interface StepState {
  visited: boolean;
}

export interface SeriesTokenMetadata {
  [key: string]: unknown;
}

export type SeriesToken = SeriesTokenMetadata & {
  imageEntry?: Entry;
  imageUrl?: string | null;
  animationEntry?: Entry;
  animationUrl?: Entry;
};

export interface CreateCollectionState {
  draftId?: string;
  collectionType?: _CollectionType;
  deploying: boolean;
  currentStep: number;
  furthestStep: number;
  steps: Record<number, StepState>;
  unzipped: boolean;
  series: {
    // Tokens already uploaded and process from a previous zip file
    uploadedTokens?: SeriesToken[];
    // New tokens from user's local zip file if upload succeeds
    newTokens: SeriesToken[];
    // New media URL if upload succeeds
    newMediaUrl: string | null;
    // New token count if upload succeeds
    newTokenImageCount: number | null;
  };
  fullWidthMode: boolean;
  fullWidthCloseBtn: boolean;
  draftProgress?: DraftProgress;
}

const stepByType: Record<DraftProgress, number | null> = {
  [DraftProgress.SELECTED_TYPE]: 0,
  [DraftProgress.ADDED_METADATA]: 1,
  [DraftProgress.ADDED_DETAILS]: 2,
  [DraftProgress.ADDED_DISTRIBUTION]: 3,
  [DraftProgress.INITIATED_DEPLOY]: 4,
  [DraftProgress.AWAITING_SIGNATURE]: 4,
  [DraftProgress.DEPLOY_FAILED]: 4,
  [DraftProgress.FINISHED]: 4,
  [DraftProgress.TESTED_GEN_SCRIPT]: null,
  [DraftProgress.ADDED_GEN_SIZE]: null,
  [DraftProgress.ADDED_GEN_PREVIEW]: null,
};

const genSeriesStepByType: Record<DraftProgress, number | null> = {
  [DraftProgress.SELECTED_TYPE]: 0,
  [DraftProgress.ADDED_METADATA]: 1,
  [DraftProgress.TESTED_GEN_SCRIPT]: 2,
  [DraftProgress.ADDED_GEN_SIZE]: 3,
  [DraftProgress.ADDED_GEN_PREVIEW]: 4,
  [DraftProgress.ADDED_DETAILS]: 5,
  [DraftProgress.ADDED_DISTRIBUTION]: 6,
  [DraftProgress.INITIATED_DEPLOY]: 7,
  [DraftProgress.AWAITING_SIGNATURE]: 7,
  [DraftProgress.DEPLOY_FAILED]: 7,
  [DraftProgress.FINISHED]: 7,
};

const editionStepByType: Record<DraftProgress, number | null> = {
  [DraftProgress.SELECTED_TYPE]: 0,
  [DraftProgress.ADDED_METADATA]: 1,
  [DraftProgress.ADDED_DETAILS]: 1,
  [DraftProgress.ADDED_DISTRIBUTION]: 1,
  [DraftProgress.INITIATED_DEPLOY]: 1,
  [DraftProgress.AWAITING_SIGNATURE]: 1,
  [DraftProgress.DEPLOY_FAILED]: 1,
  [DraftProgress.FINISHED]: 1,
  [DraftProgress.TESTED_GEN_SCRIPT]: null,
  [DraftProgress.ADDED_GEN_SIZE]: null,
  [DraftProgress.ADDED_GEN_PREVIEW]: null,
};

export const getStepByType = (
  step: DraftProgress,
  collectionType?: _CollectionType
) => {
  if (collectionType === _CollectionType.Series) {
    return stepByType[step];
  }

  if (collectionType === _CollectionType.GenerativeSeries) {
    return genSeriesStepByType[step];
  }

  return editionStepByType[step];
};

const emptyData: CreateCollectionState = {
  deploying: false,
  currentStep: 0,
  furthestStep: 0,
  steps: {
    0: { visited: true },
    1: { visited: false },
    2: { visited: false },
    3: { visited: false },
    4: { visited: false },
    5: { visited: false },
    6: { visited: false },
    7: { visited: false },
    8: { visited: false },
  },
  unzipped: false,
  series: {
    newTokens: [],
    newMediaUrl: null,
    newTokenImageCount: null,
  },
  fullWidthMode: false,
  fullWidthCloseBtn: true,
};
const initialState = emptyData;

export type CollectionUseForm = UseFormReturnType<
  CreateCollectionForm,
  (values: CreateCollectionForm) => CreateCollectionForm
>;

type Action =
  | {
      type: "UPDATE_SERIES";
      payload: Partial<CreateCollectionState["series"]>;
    }
  | {
      type: "UPDATE_UNZIPPED";
      payload: {
        unzipped: boolean;
      };
    }
  | {
      type: "NEXT_STEP";
      payload: {
        collectionType?: _CollectionType;
        form: CollectionUseForm;
      };
    }
  | {
      type: "JUMP_TO_STEP";
      payload: {
        collectionType?: _CollectionType;
        currentStep: number;
        form: CollectionUseForm;
      };
    }
  | {
      type: "LOAD_DRAFT";
      payload: {
        collectionType: _CollectionType;
        furthestStep: number;
        draftId: string;
        unzipped: boolean;
        uploadedTokens: SeriesToken[];
        draftProgress: DraftProgress;
      };
    }
  | {
      type: "UPDATE_DRAFT";
      payload: {
        draftId: string;
        unzipped: boolean;
        draftProgress: DraftProgress;
      };
    }
  | {
      type: "UPDATE_DRAFT_PROGRESS";
      payload: {
        draftProgress: DraftProgress;
      };
    }
  | {
      type: "CREATE_DRAFT";
      payload: {
        draftId: string;
      };
    }
  | {
      type: "DEPLOY_COLLECTION";
    }
  | {
      type: "SET_FULL_WIDTH";
      payload: {
        enable: boolean;
        showCloseButton?: boolean;
      };
    };

type CreateCollectionStateContext = {
  state: CreateCollectionState;
  dispatch: Dispatch<Action>;
};

function reducer(
  state: CreateCollectionState,
  action: Action
): CreateCollectionState {
  const nextStep = state.currentStep + 1;

  switch (action.type) {
    case "UPDATE_SERIES":
      return {
        ...state,
        series: {
          ...state.series,
          ...action.payload,
        },
      };
    case "UPDATE_UNZIPPED":
      return {
        ...state,
        unzipped: action.payload.unzipped,
      };
    case "NEXT_STEP":
      return {
        ...state,
        collectionType: action.payload.collectionType || state.collectionType,
        steps: validateAndCreateChangeStepState(
          state.steps,
          state.currentStep,
          action.payload.form
        ),
        currentStep: nextStep,
        furthestStep:
          state.furthestStep > nextStep ? state.furthestStep : nextStep,
      };
    case "JUMP_TO_STEP":
      const jumpStep = action.payload.currentStep;
      return {
        ...state,
        collectionType: action.payload.collectionType || state.collectionType,
        steps: validateAndCreateChangeStepState(
          state.steps,
          state.currentStep,
          action.payload.form
        ),
        currentStep: jumpStep,
      };
    case "LOAD_DRAFT":
      const { uploadedTokens, ...rest } = action.payload;
      const currentStep =
        state.currentStep ||
        Math.min(
          (action.payload.furthestStep ?? 0) + 1,
          getStepByType(
            DraftProgress.FINISHED,
            rest.collectionType || state.collectionType
          ) ?? 1
        );

      // Makes only the steps below current visited. Can be improved by storing visited steps in DB
      // or checking the load entity to get what was modified.
      const steps = (Object.keys(state.steps) as unknown as number[]).reduce(
        (acc, curr) => {
          acc[curr] = {
            visited: state.steps[curr].visited || curr < currentStep,
          };
          return acc;
        },
        {} as Record<number, StepState>
      );

      return {
        ...state,
        ...rest,
        series: {
          ...state.series,
          uploadedTokens,
        },
        steps,
        currentStep,
      };

    case "UPDATE_DRAFT":
      return { ...state, ...action.payload };
    case "UPDATE_DRAFT_PROGRESS":
      return { ...state, ...action.payload };
    case "CREATE_DRAFT":
      return { ...state, ...action.payload };
    case "DEPLOY_COLLECTION":
      return {
        ...state,
        deploying: true,
      };
    case "SET_FULL_WIDTH":
      return {
        ...state,
        fullWidthMode: action.payload.enable,
        fullWidthCloseBtn: action.payload.showCloseButton ?? true,
      };
    default:
      throw new Error("unknown reducer-action type");
  }
}

const [
  CreateCollectionFormProvider,
  useCreateCollectionFormContext,
  useCreateCollectionForm,
] = createFormContext<CreateCollectionForm>();

const ctx = createContext<CreateCollectionStateContext>({
  state: initialState,
  dispatch: () => null,
});

export const DEFAULT_PREVIEW_SIZE = 2048;

const REUSABLE_CONTRACT_COLLECTION_TYPES = [
  _CollectionType.OneOfOne,
  _CollectionType.LimitedEdition,
];

export const defaultCaptureSettings: CaptureSettings = {
  trigger: CapturePreviewTriggerType.Delay,
  delay: 3,
  viewPort: { height: DEFAULT_PREVIEW_SIZE, width: DEFAULT_PREVIEW_SIZE },
  selector: "",
  selectorType: "ELEMENT",
};

const CreateCollectionContextProvider = (props: PropsWithChildren) => {
  const [state, dispatch] = useReducer<Reducer<CreateCollectionState, Action>>(
    reducer,
    initialState
  );
  const { walletAddress } = useAuth();

  const metadataStep = getStepByType(
    DraftProgress.ADDED_METADATA,
    state.collectionType
  );
  const sizeAndOutputStep = getStepByType(
    DraftProgress.ADDED_GEN_SIZE,
    state.collectionType
  );
  const detailsStep = getStepByType(
    DraftProgress.ADDED_DETAILS,
    state.collectionType
  );
  const distributionStep = getStepByType(
    DraftProgress.ADDED_DISTRIBUTION,
    state.collectionType
  );
  const reviewStep = getStepByType(
    DraftProgress.FINISHED,
    state.collectionType
  );

  const shouldValidateMetadata =
    state.currentStep === metadataStep ||
    state.currentStep === reviewStep ||
    (metadataStep ? state.steps[metadataStep].visited : false);
  const shouldValidateSizeAndOutput =
    state.currentStep === sizeAndOutputStep ||
    state.currentStep === reviewStep ||
    (sizeAndOutputStep ? state.steps[sizeAndOutputStep].visited : false);
  const shouldValidateDetails =
    state.currentStep === detailsStep ||
    state.currentStep === reviewStep ||
    (detailsStep ? state.steps[detailsStep].visited : false);

  const shouldValidateDistribution =
    state.currentStep === distributionStep ||
    state.currentStep === reviewStep ||
    (distributionStep ? state.steps[distributionStep].visited : false);

  const form = useCreateCollectionForm({
    initialValues: {
      collectionType: _CollectionType.OpenEdition,
      gateId: null,
      withoutMintVector: false,
      metadata: {
        mediaUrl: null,
        coverMediaUrl: null,
        mediaType: SupportedMediaType.IMAGE,
        traits: {},
        seriesSize: 0,
        reveal: true,
        showAllMetadata: true,
        previewImages: [],
        generateNameTrait: false,
      },
      collection: {
        name: null,
        description: null,
        chainId: "",
        size: null,
        tags: undefined,
        categories: undefined,
      },
      contract: {
        type: "new",
        edited: false,
        data: {
          name: "",
          symbol: "",
          usingOpenseaBlocklist: false,
          collectionLogoImage: "",
          contractStandard: NftContractStandard.ERC721,
        },
      },
      distribution: {
        type: TokenDistributionType.Mint,
        price: 0,
        currency: ethers.constants.AddressZero,
        saleDateFrom: new Date(),
        saleDateTo: null,
        priceType: PriceType.Fixed,
        scheduledPrices: undefined,
        priceDropInterval: undefined,
        isDirectMint: true,
        isFreePrice: true,
        sponsoredMintsEnabled: true,
      },
      moduleSettings: {
        shouldLimitNumberOfMintPerWallet: false,
        limitPerWallet: 1,
        nonTransferable: false,
        royalty: {
          royaltyAmount: 0,
          payoutEnsName: null,
          payoutEoa: null,
          address: null,
        },
        customPaymentRecipient: {
          ensName: null,
          eoa: walletAddress || null,
          address: walletAddress || null,
        },
      },
      generativeDetails: {
        editionType: GenerativeEditionType.Limited,
        captureSettings: defaultCaptureSettings,
      },
    },
    validate: {
      metadata: {
        mediaUrl: (value, values) => {
          if (shouldValidateMetadata) {
            return [
              _CollectionType.Series,
              _CollectionType.GenerativeSeries,
            ].includes(values.collectionType)
              ? !values.metadata.mediaUrl
                ? "Upload zip media"
                : null
              : !values.metadata.mediaUrl
              ? "Add collection media"
              : null;
          }
        },
        seriesSize: (value, values) => {
          if (
            shouldValidateMetadata &&
            values.collectionType === _CollectionType.Series
          ) {
            return !values.metadata.seriesSize || values.metadata.seriesSize < 2
              ? "Upload zip file with more than 1 token"
              : null;
          }
        },
        coverMediaUrl: (value, values) => {
          if (
            shouldValidateMetadata &&
            ![
              _CollectionType.Series,
              _CollectionType.GenerativeSeries,
            ].includes(values.collectionType)
          ) {
            return values.metadata.mediaType !== SupportedMediaType.IMAGE &&
              isEmpty(values.metadata.coverMediaUrl)
              ? "Add cover image media"
              : null;
          }
        },
      },
      collection: {
        name: (value, values) => {
          if (shouldValidateDetails) {
            return !values.collection.name
              ? "Add name"
              : values.collection.name.length < 3
              ? "Too short name"
              : values.collection.name.length > 80
              ? "Too long name"
              : null;
          }
        },
        description: (value, values) => {
          if (shouldValidateDetails) {
            //TODO: use LD FeatureFlag for Description lengths
            return !values.collection.description
              ? "Add description"
              : values.collection.description.length < 3
              ? "Too short description"
              : values.collection.description.length > 10000
              ? "Too long description"
              : null;
          }
        },
        chainId: (value, values) => {
          if (
            value === "5000" &&
            values.contract.data.contractStandard ===
              NftContractStandard.ERC1155
          ) {
            return "ERC-1155 are currently not supported on Mantle";
          }
          return null;
        },
        size: (value, values) => {
          if (!shouldValidateDetails && !shouldValidateSizeAndOutput) {
            return null;
          }
          let errorMsg;
          if (
            values.collectionType === _CollectionType.LimitedEdition ||
            (values.collectionType === _CollectionType.GenerativeSeries &&
              values.generativeDetails?.editionType ===
                GenerativeEditionType.Limited) ||
            values.distribution.priceType === PriceType.RankedAuction
          ) {
            errorMsg =
              !values.collection.size || values.collection.size < 1
                ? "Add edition size"
                : null;
          }
          if (
            !errorMsg &&
            values.collectionType === _CollectionType.GenerativeSeries
          ) {
            if (
              values.generativeDetails?.curateOutput &&
              values.collection.size !==
                values.generativeDetails.allowedHashes?.length &&
              values.generativeDetails.editionType !==
                GenerativeEditionType.Open
            ) {
              errorMsg =
                "The number of selected hashes must equal the edition size";
            }
          }

          return errorMsg;
        },
      },
      contract: {
        // not sure why this is an issue here
        /* eslint-disable  @typescript-eslint/no-explicit-any */
        type: (value: any, values: CreateCollectionForm) => {
          if (
            shouldValidateDetails &&
            // Validate "type" only for the collection type that supports contract reuse
            REUSABLE_CONTRACT_COLLECTION_TYPES.includes(values.collectionType)
          ) {
            return (values.contract.type === "existing" &&
              !values.contract.data.contractId) ||
              (values.contract.type === "new" &&
                (!values.contract.data.name || !values.contract.data.symbol))
              ? "Select or create contract"
              : null;
          }
        },
        data: {
          collectionLogoImage: (value, values) => {
            if (shouldValidateDetails) {
              return values.contract.type === "new" &&
                !values.contract.data.collectionLogoImage &&
                [
                  _CollectionType.Series,
                  _CollectionType.GenerativeSeries,
                ].includes(values.collectionType)
                ? "Select a collection logo image"
                : null;
            }
          },
          name: (value, values) => {
            if (
              shouldValidateDetails &&
              // These collection types are already validated in the "type" field
              !REUSABLE_CONTRACT_COLLECTION_TYPES.includes(
                values.collectionType
              )
            ) {
              if ("contractId" in values.contract.data) {
                return null;
              }
              return !values.contract.data.name
                ? "Add contract name"
                : values.contract.data.name.length < 3
                ? "Contract name should have more than 3 characters"
                : null;
            }
          },
          symbol: (value, values) => {
            if (
              shouldValidateDetails &&
              // These collection types are already validated in the "type" field
              !REUSABLE_CONTRACT_COLLECTION_TYPES.includes(
                values.collectionType
              )
            ) {
              if ("contractId" in values.contract.data) {
                return null;
              }
              return !values.contract.data.symbol
                ? "Add contract symbol"
                : null;
            }
          },
        },
      },
      distribution: {
        currency: (value, values) => {
          if (
            shouldValidateDistribution &&
            !values.withoutMintVector &&
            values.distribution.priceType === PriceType.Fixed
          ) {
            return values.distribution.currency === null
              ? "Currency is required"
              : null;
          }
        },
        price: (value, values) => {
          if (
            shouldValidateDistribution &&
            !values.withoutMintVector &&
            values.distribution.priceType === PriceType.Fixed
          ) {
            return values.distribution.price === undefined ||
              values.distribution.price === null ||
              values.distribution.price < 0
              ? "Add price"
              : null;
          }
        },
        minBid: (value, values) => {
          if (
            shouldValidateDistribution &&
            !values.withoutMintVector &&
            values.distribution.priceType === PriceType.RankedAuction
          ) {
            return values.distribution.minBid === undefined ||
              values.distribution.minBid === null ||
              values.distribution.minBid < 0
              ? "Add minimum bid"
              : null;
          }
        },
        scheduledPrices: (value, values) => {
          if (
            shouldValidateDistribution &&
            !values.withoutMintVector &&
            values.distribution.priceType === PriceType.DutchAuction
          ) {
            return !values.distribution.scheduledPrices?.length
              ? "Add a list of prices"
              : values.distribution.scheduledPrices.length < 2
              ? "You must add more than 1 price for a Dutch Auction"
              : null;
          }
        },
        priceDropInterval: (value, values) => {
          if (
            shouldValidateDistribution &&
            !values.withoutMintVector &&
            values.distribution.priceType === PriceType.DutchAuction
          ) {
            return !values.distribution.priceDropInterval ||
              !values.distribution.priceDropInterval.some((drop) => drop.value)
              ? "Add a price drop interval"
              : values.distribution.saleDateTo &&
                values.distribution.saleDateFrom &&
                values.distribution.scheduledPrices?.length &&
                values.distribution.saleDateTo.getTime() -
                  (values.distribution.saleDateFrom.getTime() +
                    getPriceDropIntervalTime(
                      values.distribution.priceDropInterval
                    ) *
                      (values.distribution.scheduledPrices.length - 1)) <
                  0
              ? "Drop interval cannot exceed the end time"
              : null;
          }
        },
        saleDateFrom: (value, values) => {
          if (shouldValidateDistribution && !values.withoutMintVector) {
            return !values.distribution.saleDateFrom
              ? "Add start date"
              : values.distribution.saleDateTo &&
                values.distribution.saleDateTo <
                  values.distribution.saleDateFrom
              ? "Start date must be before end date"
              : null;
          }
        },
        saleDateTo: (value, values) => {
          if (shouldValidateDistribution && !values.withoutMintVector) {
            // End date is required for english and ranked auctions
            if (
              !values.distribution.saleDateTo &&
              ((values.distribution.type === TokenDistributionType.Auction &&
                values.distribution.priceType === PriceType.Fixed) ||
                values.distribution.priceType === PriceType.RankedAuction)
            ) {
              return "Add end date";
            }
            return values.distribution.saleDateTo &&
              values.distribution.saleDateTo < values.distribution.saleDateFrom
              ? "End date must be after start date"
              : null;
          }
        },
      },
      moduleSettings: {
        royalty: {
          royaltyAmount: (value, values) => {
            if (
              shouldValidateDistribution &&
              !values.moduleSettings.nonTransferable
            ) {
              return values.moduleSettings.royalty.payoutEoa &&
                values.moduleSettings.royalty.payoutEoa !==
                  ethers.constants.AddressZero &&
                !values.moduleSettings.royalty.royaltyAmount
                ? "Add royalty amount or remove recipient"
                : values.moduleSettings.royalty.royaltyAmount &&
                  values.moduleSettings.royalty.royaltyAmount <= 0
                ? "Add valid royalty amount"
                : null;
            }
          },

          address: (value, values) => {
            if (
              shouldValidateDistribution &&
              !values.moduleSettings.nonTransferable
            ) {
              return values.moduleSettings.royalty.payoutEoa &&
                !ethers.utils.isAddress(values.moduleSettings.royalty.payoutEoa)
                ? "Add valid address/ENS"
                : values.moduleSettings.royalty.royaltyAmount &&
                  (!ethers.utils.isAddress(
                    values.moduleSettings.royalty.payoutEoa || ""
                  ) ||
                    values.moduleSettings.royalty.payoutEoa ===
                      ethers.constants.AddressZero)
                ? "Add royalty recipient"
                : null;
            }
          },
        },
        customPaymentRecipient: {
          address: (value, values) => {
            if (shouldValidateDistribution) {
              return values.moduleSettings.customPaymentRecipient?.eoa &&
                !ethers.utils.isAddress(
                  values.moduleSettings.customPaymentRecipient.eoa ?? ""
                )
                ? "Add valid address/ENS"
                : null;
            }
          },
        },
      },
    },
  });

  return (
    <CreateCollectionFormProvider form={form}>
      <ctx.Provider value={{ state, dispatch }} {...props} />
    </CreateCollectionFormProvider>
  );
};

const useCreateCollectionState = () => {
  const stateContext = useContext(ctx);

  if (stateContext === undefined) {
    throw new Error(`No provider for CreateCollectionStateContext given`);
  }
  return {
    ...stateContext,
  };
};

/**
 * Validates the current step and makes it visited.
 * FIXME: Try to move this to next/jump step buttons and avoid side effects in context/reducer
 */
const validateAndCreateChangeStepState = (
  steps: Record<number, StepState>,
  currentStep: number,
  form: CollectionUseForm
): Record<number, StepState> => {
  form.validate();
  return {
    ...steps,
    [currentStep]: {
      ...steps[currentStep],
      visited: true,
    },
  };
};

/**
 * Generates a regular expression pattern for matching paths.
 *
 * @param {string[]} paths - An array of paths to generate the pattern for.
 *
 * @example
 *
 * const paths = ['path.one', 'path.two'];
 * const pathRegex = pathMatchRegex(paths);
 *
 * // Matching examples
 * pathRegex.test('path.one');       // true
 * pathRegex.test('path.two');       // true
 * pathRegex.test('path.one.more');  // true
 *
 * // Non-matching examples
 * pathRegex.test('path');           // false
 * pathRegex.test('path1');          // false
 * pathRegex.test('another.path');   // false
 */
export const pathMatchRegex = (paths: string[]) =>
  new RegExp(
    `^(${
      paths
        // Escapes all '.' characters in each path
        .map((path) => path.replace(/\./g, "\\."))
        // Joins all paths together using a '|' separator
        .join("|")
      // Matches either a '.' character or the end of the string after the path
    })(\\.|$)`
  );

const standardValidations: Record<number, string[]> = {
  1: ["metadata.mediaUrl", "metadata.seriesSize", "metadata.coverMediaUrl"],
  2: [
    "collection.name",
    "collection.description",
    "collection.size",
    "contract",
  ],
  3: [
    "distribution.price",
    "distribution.minBid",
    "distribution.scheduledPrices",
    "distribution.priceDropInterval",
    "distribution.saleDateFrom",
    "distribution.saleDateTo",
    "moduleSettings.royalty.royaltyAmount",
    "moduleSettings.royalty.address",
    "moduleSettings.customPaymentRecipient.address",
  ],
  4: [""],
};

const editionValidations: Record<number, string[]> = {
  1: [
    "metadata.mediaUrl",
    "metadata.seriesSize",
    "metadata.coverMediaUrl",

    "collection.name",
    "collection.description",
    "collection.size",
    "contract",

    "distribution.price",
    "distribution.minBid",
    "distribution.scheduledPrices",
    "distribution.priceDropInterval",
    "distribution.saleDateFrom",
    "distribution.saleDateTo",
    "moduleSettings.royalty.royaltyAmount",
    "moduleSettings.royalty.address",
    "moduleSettings.customPaymentRecipient.address",
  ],
  2: [""],
};

const codeGenValidations: Record<number, string[]> = {
  1: ["metadata.mediaUrl"],
  2: [""],
  3: ["collection.size"],
  4: [""],
  5: ["collection.name", "collection.description", "contract"],
  6: standardValidations[3],
  7: [""],
};

export const getValidationPathMapping = (
  collectionType: _CollectionType,
  step: number
) => {
  if (collectionType === _CollectionType.GenerativeSeries) {
    return codeGenValidations[step];
  }
  if (collectionType === _CollectionType.Series) {
    return standardValidations[step];
  }

  return editionValidations[step];
};

const findPath = (path: string, mapping: Record<number, string[]>) =>
  Object.entries(mapping).find(([, paths]) =>
    path.match(pathMatchRegex(paths))
  )?.[0];

export const getStepByPath = (
  path: string,
  collectionType: _CollectionType
) => {
  const foundPath = findPath(
    path,
    collectionType === _CollectionType.GenerativeSeries
      ? codeGenValidations
      : standardValidations
  );
  return foundPath ? +foundPath : -1;
};

export { CreateCollectionContextProvider, useCreateCollectionState };
export { useCreateCollectionFormContext };
